import React from 'react'
import styled, { keyframes, css } from 'styled-components'
import { rem } from 'polished'

// Exact Y positions for each digit based on SVG path coordinates
const DIGIT_Y_POSITIONS = [
  0, // 0
  102.6, // 1
  204.5, // 2
  306.8, // 3
  410.5, // 4
  512.8, // 5
  611.5, // 6
  717.3, // 7
  818.1, // 8
  920.3, // 9
  1022.6, // 0 (repeat)
  1125.5, // 1 (repeat)
  1227.1, // 2 (repeat)
  1329.4, // 3 (repeat)
  1433.1, // 4 (repeat)
  1535.4, // 5 (repeat)
]

const VIEWBOX_WIDTH = 56
// Total height to include all digits we need (16 positions)
const TOTAL_SVG_HEIGHT = 1600

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${rem(2)};
  min-height: ${rem(66)};
`

const SlotWindow = styled.div`
  width: ${rem(56)};
  height: ${rem(66)};
  overflow: hidden;
  display: inline-block;
`

// Create keyframes for each reel configuration
const createSpinAnimation = (targetPosition: number) => keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(${-targetPosition}px);
  }
`

// SVG paths for digits 0-9, repeated twice for smooth spinning
const SVG_PATHS = (
  <>
    <path d="M21.4004 61.2713C37.5917 61.2713 45.0057 31.5304 45.0057 17.3843C45.0057 8.94783 42.2787 3.06783 35.1204 3.06783C18.6735 3.06783 11.43 32.6383 11.43 46.8696C11.43 55.3913 14.2422 61.2713 21.4004 61.2713ZM2.22653 42.0122C2.22653 23.2643 15.1796 0 35.2909 0C48.1587 0 54.2944 9.71479 54.2944 22.0713C54.2944 40.9896 41.4265 64.3391 21.1448 64.3391C8.36218 64.3391 2.22653 54.4539 2.22653 42.0122Z" fill="white" />
    <path d="M41.8153 102.943L29.3736 153.136C27.1579 161.998 27.0727 163.106 34.4014 163.958L34.0606 165.492H7.89881L8.3249 163.788C17.9545 162.85 18.2101 161.998 20.4258 153.136L30.311 113.68H18.2953L18.8066 111.55C29.9701 110.527 35.3388 107.715 39.9406 102.943H41.8153Z" fill="white" />
    <path d="M-1.28036 267.753L-0.939495 266.39C31.1022 240.654 39.7944 230.002 39.7944 220.543C39.7944 214.066 34.5109 211.254 29.3127 211.254C22.4953 211.254 16.5301 214.066 14.2292 221.991H11.7579C14.2292 210.317 23.7735 204.522 34.5962 204.522C43.0327 204.522 50.3614 209.294 50.3614 217.39C50.3614 226.678 40.4762 235.37 27.6083 246.108C26.4153 247.13 13.0362 258.294 11.8431 259.231H41.7544L39.624 267.753H-1.28036Z" fill="white" />
    <path d="M17.5031 371.122C11.1118 371.122 4.55004 369.673 2.07873 368.395L5.23177 355.612H6.93612C7.19178 363.793 10.6005 367.713 18.5257 367.713C27.3031 367.713 34.9726 361.492 34.9726 352.289C34.9726 346.409 30.9674 340.614 21.6787 338.569L22.19 336.694L24.3205 335.842C35.6544 331.666 40.2561 326.127 40.2561 320.843C40.2561 316.071 36.677 313.43 30.4561 313.43C23.8944 313.43 17.4179 316.753 15.0318 324.252H12.5605C15.884 311.896 24.917 306.783 35.3135 306.783C43.5796 306.783 50.3118 310.873 50.3118 317.35C50.3118 325.36 41.1083 330.303 33.6944 333.541L33.6092 333.797C40.6822 336.95 45.1987 342.659 45.1987 348.965C45.1987 362.941 29.5187 371.122 17.5031 371.122Z" fill="white" />
    <path d="M33.5383 472.275H24.5904L28.7661 455.487H-0.974781L-0.548694 453.612L41.5487 410.492H48.8774L39.5035 448.073H49.9L48.1104 455.487H37.7139L33.5383 472.275ZM30.5557 448.073L37.7991 419.27L9.67739 448.073H30.5557Z" fill="white" />
    <path d="M12.1204 538.744L27.8856 512.753H54.9847L50.0421 521.275H26.4369L20.4717 530.904C36.9187 535.08 43.7361 544.028 43.7361 553.317C43.7361 568.656 28.6526 575.643 15.6995 575.643C9.81953 575.643 3.25779 574.195 0.616055 572.576L4.02475 559.197H5.64388C5.64388 569.082 10.5865 572.235 17.2334 572.235C25.3291 572.235 33.8508 568.23 33.8508 557.833C33.8508 550.845 29.4195 542.409 12.1204 538.744Z" fill="white" />
    <path d="M49.912 650.805C49.912 665.463 37.0442 677.99 21.705 677.99C10.7972 677.99 3.80938 671.087 3.80938 658.645C3.80938 635.125 29.7155 618.423 55.5363 611.52L56.3885 613.991C39.6007 618.593 24.9433 629.501 17.9555 643.903C23.3242 638.619 28.5224 635.637 34.9989 635.637C43.8616 635.637 49.912 642.198 49.912 650.805ZM29.6302 640.835C25.0285 640.835 20.6824 642.539 17.1033 645.777C14.8876 650.976 13.6094 656.6 13.6094 662.48C13.6094 670.32 16.3363 674.922 22.2163 674.922C32.3572 674.922 39.5155 662.906 39.5155 652.424C39.5155 646.459 36.9589 640.835 29.6302 640.835Z" fill="white" />
    <path d="M6.79132 779.057C11.8191 750.339 35.0835 735.17 53.0644 725.03H12.5861L14.4609 717.275H58.0922L56.2174 725.03C37.9809 738.238 20.8522 753.577 17.3583 779.057H6.79132Z" fill="white" />
    <path d="M1.87934 867.343C1.87934 857.883 12.3611 851.918 21.5646 849.191C17.1332 845.612 13.5541 841.181 13.5541 834.96C13.5541 823.456 24.7176 818.087 35.6254 818.087C44.9141 818.087 53.4359 822.518 53.4359 830.443C53.4359 839.477 43.0393 843.993 35.1141 846.55L36.0515 847.146C42.6985 851.918 49.0898 856.435 49.0898 864.36C49.0898 877.91 34.1767 882.426 22.5872 882.426C12.0202 882.426 1.87934 876.716 1.87934 867.343ZM9.37847 866.746C9.37847 874.33 14.1506 879.358 23.3541 879.358C31.1089 879.358 39.9715 875.95 39.9715 867.683C39.9715 862.485 35.7959 859.503 28.7228 854.39L23.8654 850.896C23.7802 850.896 23.7802 850.896 23.695 850.81C15.4289 853.878 9.37847 859.076 9.37847 866.746ZM46.2776 830.443C46.2776 824.393 41.335 821.155 35.1993 821.155C27.4446 821.155 21.3941 824.904 21.3941 831.125C21.3941 836.494 25.8254 839.732 30.4272 843.141L33.0689 845.016C40.3976 842.118 46.2776 838.198 46.2776 830.443Z" fill="white" />
    <path d="M7.07527 947.447C7.07527 932.79 19.9431 920.348 35.2822 920.348C46.1901 920.348 53.1779 927.25 53.1779 939.607C53.1779 963.212 27.2718 980 1.45092 986.903L0.598745 984.516C17.4718 979.915 32.1292 968.836 39.117 954.35C33.6631 959.803 28.4648 962.616 21.9883 962.616C13.1257 962.616 7.07527 956.139 7.07527 947.447ZM27.357 957.503C31.9587 957.503 36.3048 955.798 39.9692 952.475C42.0996 947.276 43.3779 941.737 43.3779 935.857C43.3779 927.932 40.6509 923.416 34.7709 923.416C24.63 923.416 17.4718 935.431 17.4718 945.913C17.4718 951.793 20.0283 957.503 27.357 957.503Z" fill="white" />
    <path d="M21.4004 1083.88C37.5917 1083.88 45.0057 1054.14 45.0057 1039.99C45.0057 1031.56 42.2787 1025.68 35.1204 1025.68C18.6735 1025.68 11.43 1055.25 11.43 1069.48C11.43 1078 14.2422 1083.88 21.4004 1083.88ZM2.22653 1064.62C2.22653 1045.87 15.1796 1022.61 35.2909 1022.61C48.1587 1022.61 54.2944 1032.32 54.2944 1044.68C54.2944 1063.6 41.4265 1086.95 21.1448 1086.95C8.36218 1086.95 2.22653 1077.06 2.22653 1064.62Z" fill="white" />
    <path d="M41.8153 1125.55L29.3736 1175.74C27.1579 1184.61 27.0727 1185.71 34.4014 1186.57L34.0606 1188.1H7.89881L8.3249 1186.4C17.9545 1185.46 18.2101 1184.61 20.4258 1175.74L30.311 1136.29H18.2953L18.8066 1134.16C29.9701 1133.14 35.3388 1130.32 39.9406 1125.55H41.8153Z" fill="white" />
    <path d="M-1.28036 1290.36L-0.939495 1289C31.1022 1263.26 39.7944 1252.61 39.7944 1243.15C39.7944 1236.67 34.5109 1233.86 29.3127 1233.86C22.4953 1233.86 16.5301 1236.67 14.2292 1244.6H11.7579C14.2292 1232.93 23.7735 1227.13 34.5962 1227.13C43.0327 1227.13 50.3614 1231.9 50.3614 1240C50.3614 1249.29 40.4762 1257.98 27.6083 1268.72C26.4153 1269.74 13.0362 1280.9 11.8431 1281.84H41.7544L39.624 1290.36H-1.28036Z" fill="white" />
    <path d="M17.5031 1393.73C11.1118 1393.73 4.55004 1392.28 2.07873 1391L5.23177 1378.22H6.93612C7.19178 1386.4 10.6005 1390.32 18.5257 1390.32C27.3031 1390.32 34.9726 1384.1 34.9726 1374.9C34.9726 1369.02 30.9674 1363.22 21.6787 1361.18L22.19 1359.3L24.3205 1358.45C35.6544 1354.27 40.2561 1348.74 40.2561 1343.45C40.2561 1338.68 36.677 1336.04 30.4561 1336.04C23.8944 1336.04 17.4179 1339.36 15.0318 1346.86H12.5605C15.884 1334.5 24.917 1329.39 35.3135 1329.39C43.5796 1329.39 50.3118 1333.48 50.3118 1339.96C50.3118 1347.97 41.1083 1352.91 33.6944 1356.15L33.6092 1356.41C40.6822 1359.56 45.1987 1365.27 45.1987 1371.57C45.1987 1385.55 29.5187 1393.73 17.5031 1393.73Z" fill="white" />
    <path d="M33.5383 1494.88H24.5904L28.7661 1478.1H-0.974781L-0.548694 1476.22L41.5487 1433.1H48.8774L39.5035 1470.68H49.9L48.1104 1478.1H37.7139L33.5383 1494.88ZM30.5557 1470.68L37.7991 1441.88L9.67739 1470.68H30.5557Z" fill="white" />
    <path d="M12.1204 1561.35L27.8856 1535.36H54.9847L50.0421 1543.88H26.4369L20.4717 1553.51C36.9187 1557.69 43.7361 1566.64 43.7361 1575.93C43.7361 1591.26 28.6526 1598.25 15.6995 1598.25C9.81953 1598.25 3.25779 1596.8 0.616055 1595.18L4.02475 1581.81H5.64388C5.64388 1591.69 10.5865 1594.84 17.2334 1594.84C25.3291 1594.84 33.8508 1590.84 33.8508 1580.44C33.8508 1573.45 29.4195 1565.02 12.1204 1561.35Z" fill="white" />
  </>
)

// Pre-create animations for each reel target position
// Digit 2 after 0 spins = position index 2
// Digit 0 after 1 spin = position index 10
// Digit 2 after 1 spin = position index 12
// Digit 5 after 1 spin = position index 15
const spinTo2 = createSpinAnimation(DIGIT_Y_POSITIONS[2])
const spinTo10 = createSpinAnimation(DIGIT_Y_POSITIONS[10])
const spinTo12 = createSpinAnimation(DIGIT_Y_POSITIONS[12])
const spinTo15 = createSpinAnimation(DIGIT_Y_POSITIONS[15])

const SlotSvg = styled.svg`
  display: block;
`

const ANIMATION_DURATION = 2000

const reelGroupStyles = css`
  will-change: transform;
`

const ReelGroupTo2 = styled.g`
  ${reelGroupStyles}
  animation: ${spinTo2} ${ANIMATION_DURATION}ms cubic-bezier(.15,.85,.2,1) forwards;
`

const ReelGroupTo10 = styled.g`
  ${reelGroupStyles}
  animation: ${spinTo10} ${ANIMATION_DURATION}ms cubic-bezier(.15,.85,.2,1) forwards;
`

const ReelGroupTo12 = styled.g`
  ${reelGroupStyles}
  animation: ${spinTo12} ${ANIMATION_DURATION}ms cubic-bezier(.15,.85,.2,1) forwards;
`

const ReelGroupTo15 = styled.g`
  ${reelGroupStyles}
  animation: ${spinTo15} ${ANIMATION_DURATION}ms cubic-bezier(.15,.85,.2,1) forwards;
`

// Configuration for each reel
const REEL_CONFIG = [
  { ReelGroup: ReelGroupTo2, label: '2', key: 1 },
  { ReelGroup: ReelGroupTo10, label: '0', key: 2 },
  { ReelGroup: ReelGroupTo12, label: '2', key: 3 },
  { ReelGroup: ReelGroupTo15, label: '5', key: 4 },
]

function WrappedYearAnimation() {
  return (
    <Container>
      {REEL_CONFIG.map((config) => (
        <SlotWindow key={config.key}>
          <SlotSvg
            viewBox={`0 0 ${VIEWBOX_WIDTH} ${TOTAL_SVG_HEIGHT}`}
            width={56}
            height={TOTAL_SVG_HEIGHT}
            aria-label={`digit ${config.label}`}
          >
            <config.ReelGroup>
              {SVG_PATHS}
            </config.ReelGroup>
          </SlotSvg>
        </SlotWindow>
      ))}
    </Container>
  )
}

export default WrappedYearAnimation
